export enum HttpMethods {
  GET = "get",
  POST = "post",
  PATCH = "patch",
  PUT = "put",
  DELETE = "delete",
}

export type HttpError = {
  code: number;
  data: any;
};

// REQUESTS
export type SignupRequest = {
  name: string;
  surname: string;
  username: string;
  email: string;
  phone: string;
  password: string;
  captcha_token: string | null;
};

export type SignupActivationRequest = {
  username: string;
  auth_code: string;
};

export type RequestResetPasswordRequest = {
  email: string;
};

export type ResetPasswordRequest = {
  username: string;
  reset_code: string;
  password: string;
};

export type UpdateUserRequest = {
  name?: string;
  surname?: string;
  email?: string;
  phone?: string;
  password?: string;
};

export type BookingRequest = {
  date: Date;
  hour_id: number;
  court_id: number;
  type: BookingType;
  is_heated?: boolean;
  username?: string;
};

export type BookingUpdateRequest = {
  status?: BookingStatus;
  type?: BookingType;
  is_heated?: boolean;
};

export type BookingRenameRequest = {
  username: string;
};

export type PayBookingsRequest = {
  booking_ids: number[];
  name: string;
  item_price: number;
  item_count: number;
};

export type CopyBookingsRequest = {
  source_date: Date;
  source_court_id: number;
  target_date: Date;
  target_court_id: number;
};

export type SettingsUpdateRequest = {
  weeks_number?: number;
  weeks_history_number?: number;
  night_hour_id?: number;
  heating_hour_id?: number;
  cancel_time?: number;
  announcement_text?: string;
};

export type UpdateCourtRequest = {
  name?: string;
  is_indoor?: boolean;
};

export type CreateCourtRequest = {
  name: string;
  is_indoor?: boolean;
};

export type PriceUpdateRequest = {
  prices: { id: number; value: number }[];
};

// RESPONSES
export type AuthToken = {
  access_token: string;
  token_type: string;
};

export enum UserRole {
  USER = "UTENTE",
  ADMIN = "ADMIN",
  MASTER = "MASTER",
  SERVICE = "SERVIZIO",
  MANAGER = "MANAGER",
}

export enum Permission {
  COPY_WEEK = "copy_week",
  RENAME_BOOKING = "rename_booking",
}

export type User = {
  id: number;
  name: string;
  surname: string;
  username: string;
  email: string;
  phone: string;
  role: UserRole;
  permissions: Permission[];
};

export type UserReadModel = {
  id: number;
  name: string;
  surname: string;
  username: string;
  email: string;
  phone: string;
  role: UserRole;
  is_member: boolean;
  signup_date: string | null;
  last_booking_date: string | null;
};

export type SignupResponse = {
  user_id: number;
};

export type ValidateResetCodeResponse = {
  is_valid: boolean;
};

export enum BookingStatus {
  BOOKED = "booked",
  PAID = "paid",
  NOT_PAID = "not_paid",
  NOT_PLAYED = "not_played",
}

export enum BookingType {
  SINGLE = "single",
  DOUBLE = "double",
  SOCIAL_CHAMPIONSHIP = "social_championship",
  SERVICE = "service",
}

export type Booking = {
  id: number;
  date: string;
  hour_id: number;
  hour_label: string;
  start_at: Date;
  user_id: number;
  username: string;
  user_name: string;
  user_surname: string;
  court_id: number;
  court_label: string;
  status: BookingStatus;
  type: BookingType;
  receipt_id: number | undefined;
  is_guest: boolean;
  is_member: boolean;
  is_heated: boolean;
  created_by: string | null;
};

export type PayBookingsResponse = {
  receipt_id: number;
};

export type ReceiptPreview = {
  number: string;
  name: string;
  item_count: number;
  item_price: number;
  item_description: string;
};

export type Receipt = {
  id: number;
  number: string;
  date: Date;
  name: string;
  item_count: number;
  item_price: number;
  amount: number;
};

export type Court = {
  id: number;
  name: string;
  is_indoor: boolean;
};

export type Hour = {
  id: number;
  label: string;
};

export type Price = {
  id: number;
  description: string;
  value: number;
};

export type Settings = {
  weeks_number: number;
  weeks_history_number: number;
  cancel_time: number;
  heating_hour_id: number;
  night_hour_id: number;
  heating_hour: string;
  announcement_text: string;
  captcha_site_key: string;
};

export type ServiceHour = {
  id: number;
  name: string;
};

export type CreateServiceHourRequest = {
  name: string;
};

export type UpdateServiceHourRequest = {
  name: string;
};

export type Autobooking = {
  id: number;
  day: number;
  start_time: string;
  number_of_hours: number;
  user: {
    id: number;
    username: string;
    is_guest: boolean;
    is_service: boolean;
  };
  court: {
    id: number;
    name: string;
  };
  is_double: boolean;
  is_active: boolean;
};

export type CreateAutobookingRequest = {
  day: number;
  start_time: string;
  number_of_hours: number;
  user_id?: number;
  username?: string;
  court_id: number;
  is_double: boolean;
  is_active: boolean;
};

export type UpdateAutobookingRequest = {
  day?: number;
  start_time?: string;
  number_of_hours?: number;
  user_id?: number;
  username?: string;
  court_id?: number;
  is_double?: boolean;
  is_active?: boolean;
};

export type ManagementUpdateUserRequest = {
  user_ids: number[];
  is_member?: boolean;
  role?: UserRole;
};

export type ManagementDeleteUserRequest = {
  user_ids: number[];
};

export type BookingSummaryMonth = {
  month: number;
  year: number;
  total_bookings: number;
  total_paid_bookings: number;
  total_receipts: number;
  total_amount: number;
  total_amount_previous_year: number;
};

export type BookingSummary = {
  months: BookingSummaryMonth[];
};

export type UserBookingSummaryItem = {
  username: string;
  name: string;
  surname: string;
  total_bookings: number;
  total_paid_bookings: number;
  total_amount: number;
};

export type UnpaidBookingItem = {
  id: number;
  date: string;
  hour_label: string;
  username: string;
  court_label: string;
  amount: number;
};

export type MonthlyHoursData = {
  month: number;
  members: number;
  non_members: number;
};

export type StatisticsResponse = {
  total_bookings: number;
  total_users: number;
  total_receipts: number;
  unpaid_hours: number;
  paid_hours: number;
  paid_hours_members: number;
  monthly_hours: MonthlyHoursData[];
};
